import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "ol/ol.css";
import { Feature } from "ol";
import { transform, transformExtent } from "ol/proj";
import * as mapSettings from "../assets/map.json";
import { containsExtent, getCenter } from "ol/extent";
import { Circle } from "ol/geom";
import { Coordinate } from "ol/coordinate";
import {
    addArcgisPathsToMap,
    addMapPin,
    addParkingMachine,
    initMapLayer,
    loadParkingZonesIntoMap,
    locationMarker,
    map,
    mapDebounce,
    myLocationMarker,
    myLocationMarkerStyleBackground,
    myLocationMarkerStyleCircle,
    parkingMachines,
    parkingMachineStyle,
    parkingZones,
    styleElement,
} from "../components/Map";
import MapSearch, { FilterInitialValues } from "../components/MapSearch/MapSearch";
import MapZoom from "../components/MapZoom/MapZoom";
import MapMyLocation from "../components/MapMyLocation/MapMyLocation";
import MapResults from "../components/MapResults/MapResults";
import MapResultsSheet from "../components/MapResultsSheet/MapResultsSheet";
import DetailZoneItem from "../components/DetailZoneItem/DetailZoneItem";
import { fetchData } from "../services/api";
import { TFilter } from "../model/type/MapSearch/TFilter";
import Icon from "../components/Icon";
import styles from "./Main.module.scss";
import DetailZoneActions from "../components/DetailZoneActions/DetalZoneActions";
import { useTranslation } from "react-i18next";
import useFavoriteZone from "../model/useFavoriteZone";
import useLocalStorageState from "../hooks/useLocalStorageState";
import { usePreviousLocation } from "../hooks/PreviousLocationContext";
import { QrReader } from "react-qr-reader";
import Alert from "components/Alert/Alert";
import ArcgisService from "services/ArcgisService";
import StreetParkingService from "services/StreetParkingService";
import MapService from "services/MapService";
import { sortTariffDefinitions } from "utils/sortTariffDefinitions";
import { PARK_MACHINE_TYPE } from "../constants";
import DetailZoneName from "../components/DetailZoneItem/DetailZoneName";

const { mapZoom, mapAnimationDuration, maxZoom } = mapSettings;

const mapService = MapService.getInstance();

const Main = () => {
    const arcgisService = new ArcgisService();
    const streetParkingService = new StreetParkingService(
        window._env_.REACT_APP_API_FORCE_URL,
        window._env_.REACT_APP_API_FORCE_TOKEN
    );

    const { t } = useTranslation();

    const mapRef = useRef<HTMLDivElement>(null);
    const [isMapResultsForceClose, setIsMapResultForceClose] = useState(false);
    const [filter, setFilter] = useState<TFilter>(FilterInitialValues);
    const filterRef = useRef(filter);
    const [filteredZones, setFilteredZones] = useState([]);
    const [hideResults, setHideResults] = useState(true);
    const [detailZone, setDetailZone] = useState(null);
    const [detailZonePricing, setDetailZonePricing] = useState(null);
    const navigate = useNavigate();
    const [snapPoints, setSnapPoints] = useState([-145, 100]);
    const [mapMove, setMapMove] = useState(true);
    const [pricingDetail, setPricingDetail] = useState(false);
    const detailZoneRef = useRef(null);
    const { zones, deleteRecord: deleteZoneRecord } = useFavoriteZone();
    const [raLevelsData, setRaLevelsData] = useState([]);
    const [alert, setAlert] = useState<string | null>(null);

    const [selectedParkingMachine, setSelectedParkingMachine] = useState<null | string>(null);

    const mapViewCenter = map.getView().getCenter();
    const mapViewZoom = map.getView().getZoom();

    const [searchParams] = useSearchParams();

    // Načíst zóny které jsou označené jako oblíbené vždy
    useEffect(() => {
        zones.forEach((zone) => {
            let section = JSON.parse(zone.json);
            delete section.geometry;

            if (section.featureType === null || section.featureType === "parkingZone") {
                loadParkingZonesIntoMap([section], false);
            }

            if (section.featureType === "parkingMachine") {
                addParkingMachine(JSON.parse(section.position), "#1D4F8B", section, false);
            }
        });
    }, [zones.length]);

    useEffect(() => {
        if (detailZone) {
            fetchData("Tickets/streetParking/calculateprice", "POST", "body", {
                licensePlate: "string",
                sectionCode: detailZone.properties.featureType === "parkingZone" ? detailZone.properties.sectionCode : null,
                parkMachineCode:
                    detailZone.properties.featureType === "parkingMachine" ? detailZone.properties.parkMachineCode : null,
                tariff: detailZone.properties.tariffInfo.tariffID,
                requestedMinutes: 60,
            }).then((data) => {
                setDetailZonePricing(Number(data?.priceTotal));
            });
        }
    }, [detailZone]);

    useEffect(() => {
        const selectedFeature = mapService.getSelectedFeature();
        if (detailZone && selectedFeature) {
            const extent = selectedFeature.getGeometry().getExtent();
            const boundaries = transformExtent(extent, "EPSG:5514", "EPSG:4326");
            fetchData("StreetParking/parkMachines/2/byBoundaries", "GET", "url", {
                minLatitude: boundaries[1],
                minLongitude: boundaries[0],
                maxLatitude: boundaries[3],
                maxLongitude: boundaries[2],
            }).then((results) => {
                results.forEach((machine) => {
                    if (machine.sectionID == detailZone.properties.sectionID) {
                        let color = selectedParkingMachine === machine.parkMachineID ? "rgb(212, 9, 57)" : "#1D4F8B";
                        addParkingMachine(JSON.parse(machine.position), color, machine, false);
                    }
                });
            });
        }
    }, [detailZone]);

    useEffect(() => {
        const selectedFeature = mapService.getSelectedFeature();
        if (!detailZone || !detailZoneRef.current || !selectedFeature || !(selectedFeature instanceof Feature)) {
            return;
        }

        requestAnimationFrame(() => {
            const currentSelectedFeature = mapService.getSelectedFeature();
            if (!detailZoneRef.current || !currentSelectedFeature) return;

            const geometry = currentSelectedFeature.getGeometry();
            const featureCenter = geometry.getExtent();

            if (isMobile) {
                const bottomOffset = detailZoneRef.current.offsetHeight;
                map.getView().fit(featureCenter, {
                    size: map.getSize(),
                    padding: [10, 10, bottomOffset + 25, 10],
                    maxZoom: maxZoom,
                    duration: mapAnimationDuration,
                });
            } else {
                const leftOffset = 364;
                const rightOffset = 364;
                map.getView().fit(featureCenter, {
                    size: map.getSize(),
                    padding: [50, rightOffset + 35, 50, leftOffset + 35],
                    maxZoom: maxZoom,
                    duration: mapAnimationDuration,
                });
            }
        });
    }, [detailZone, detailZoneRef, pricingDetail, detailZoneRef.current?.offsetHeight, window.innerHeight, window.innerWidth]);

    useEffect(() => {
        filterRef.current = filter;

        const result = mapService.applyFilter(filter);
        setFilteredZones(result.visibleZones);
    }, [filter]);

    const isMobile = window.innerWidth < 575.98;
    const isDesktop = window.innerWidth >= 575.98;

    const loadDetailZone = async (section: { [p: string]: any }, hasAnimation: boolean = false) => {
        if (!section) {
            console.warn("loadDetailZone called with undefined section");
            return;
        }

        setMapMove(false);

        const featureType = section.featureType || "parkingZone";

        redirectToSection(featureType === "parkingZone" ? section.sectionCode : section.parkMachineCode, section.sectionCode);

        mapService.deselectFeature();

        let feature;

        if (featureType === "parkingZone") {
            feature = parkingZones.getSource().getFeatureById(section.sectionID);
        } else if (featureType === "parkingMachine") {
            feature = parkingMachines.getSource().getFeatureById(section.parkMachineID);
        }

        if (!feature && section.sectionBorder && featureType === "parkingZone") {
            await loadParkingZonesIntoMap([section], false);

            feature = parkingZones.getSource().getFeatureById(section.sectionID);

            if (feature instanceof Feature) {
                const geometry = feature.getGeometry();
                const extent = geometry.getExtent();

                map.getView().fit(extent, {
                    size: map.getSize(),
                    padding: [50, 50, 50, 50],
                    maxZoom: maxZoom,
                    duration: mapAnimationDuration,
                });
            }
        } else if (!feature && section.position && featureType === "parkingMachine") {
            addParkingMachine(JSON.parse(section.position), "#1D4F8B", section, false);
            feature = parkingMachines.getSource().getFeatureById(section.parkMachineID);

            if (feature instanceof Feature) {
                const extent = feature.getGeometry().getExtent();
                map.getView().fit(extent, {
                    size: map.getSize(),
                    padding: [100, 100, 100, 100],
                    maxZoom: maxZoom,
                    duration: mapAnimationDuration,
                });
            }
        }

        if (feature instanceof Feature) {
            mapService.selectFeature(feature);

            if (feature.getProperties().featureType === "parkingZone") {
                feature.setStyle(styleElement(section.sectionCategoryColor, true));
            }

            if (feature.getProperties().featureType === "parkingMachine") {
                feature.setStyle(parkingMachineStyle("#d40939", feature));
            }
        }

        if (!hasAnimation) {
            if (feature === null) {
                setTimeout(() => {
                    if (featureType === "parkingZone") {
                        feature = parkingZones.getSource().getFeatureById(section.sectionID);
                    } else {
                        feature = parkingMachines.getSource().getFeatureById(section.parkMachineID);
                    }
                    if (feature instanceof Feature) {
                        mapService.selectFeature(feature);
                        if (feature.getProperties().featureType === "parkingZone") {
                            feature.setStyle(styleElement(section.sectionCategoryColor, true));
                        }

                        if (feature.getProperties().featureType === "parkingMachine") {
                            feature.setStyle(parkingMachineStyle("#d40939", feature));
                        }
                    }
                }, 500);
            }
        }

        let cordsDetail: Coordinate;

        if (feature instanceof Feature) {
            let geometry = feature.getGeometry();
            cordsDetail = transform(getCenter(geometry.getExtent()), "EPSG:5514", "EPSG:4326");
        }

        setDetailZone({
            coordinates: cordsDetail,
            properties: { ...section, featureType },
        });

        setIsMapResultForceClose(true);
    };

    useEffect(() => {
        const { query } = filter;

        if (query.value.startsWith("mapbox_")) {
            const isSpecificZone = query.section;
            const coords = query.value.replace("mapbox_", "").split(",");
            const paths = query?.paths || [];
            const section = query.section;
            arcgisService
                .getSuggestedFeature({
                    name: query.label,
                    address: query.label,
                    location: {
                        x: parseFloat(coords[0]),
                        y: parseFloat(coords[1]),
                    },
                    paths: paths,
                })
                .then((feature) => {
                    const coords3857 = transform(feature.features[0].geometry.coordinates, "EPSG:4326", "EPSG:5514");
                    map.getView().animate({
                        center: coords3857,
                        zoom: maxZoom,
                        duration: mapAnimationDuration,
                    });
                    try {
                        loadDetailZone(section);
                    } catch (error) {
                        console.error("Error loading detail zone:", error);
                    }
                    locationMarker.getSource().clear();
                    addMapPin(feature.features[0]);
                    !isSpecificZone && addArcgisPathsToMap(feature.features[0].geometry.paths, feature.features[0].geometry.type);
                });
        } else if (query.value.startsWith("api_")) {
            streetParkingService.getStreetParkingSection(query.value.replaceAll("api_", "")).then(async (results) => {
                if (!results[0]) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                await loadParkingZonesIntoMap(results, false);

                const feature = parkingZones.getSource().getFeatureById(results[0].sectionID);

                if (!feature) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                if (feature instanceof Feature) {
                    const extent = feature.getGeometry().getExtent();
                    map.getView().fit(extent, {
                        size: map.getSize(),
                        padding: [50, 50, 50, 50],
                        maxZoom: maxZoom,
                        duration: mapAnimationDuration,
                    });

                    loadDetailZone(feature.getProperties(), false);
                    mapService.selectFeature(feature);
                    redirectToSection(feature.getProperties(), feature.getProperties().sectionCode);
                    const geometry = feature.getGeometry();
                    const coordinates: Coordinate = transform(getCenter(geometry.getExtent()), "EPSG:5514", "EPSG:4326");
                    setDetailZone({
                        coordinates: coordinates,
                        properties: feature.getProperties(),
                    });
                }
            });
        } else if (query.value.startsWith("pm_")) {
            fetchData("StreetParking/parkMachines/2", "GET", "url", {
                parkMachineCode: query.value.replaceAll("pm_", ""),
            }).then((parkingMachinesList) => {
                if (!parkingMachinesList[0]) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                parkingMachinesList.forEach((machine) => {
                    addParkingMachine(JSON.parse(machine.position), "#1D4F8B", machine, false);
                });

                const feature = parkingMachines.getSource().getFeatureById(parkingMachinesList[0].parkMachineID);

                if (!feature) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                if (feature instanceof Feature) {
                    const extent = feature.getGeometry().getExtent();
                    map.getView().fit(extent, {
                        size: map.getSize(),
                        padding: [100, 100, 100, 100],
                        maxZoom: maxZoom,
                        duration: mapAnimationDuration,
                    });

                    const properties = feature.getProperties();
                    mapService.selectFeature(feature);
                    feature.setStyle(parkingMachineStyle("#d40939", feature));
                    const coordinates = getCenter(feature.getGeometry().getExtent());
                    setDetailZone({
                        coordinates: coordinates,
                        properties: feature.getProperties(),
                    });
                    redirectToSection(properties, properties.parkMachineCode, "parkingMachine");
                }
            });
        } else if (query.value.startsWith("ib_")) {
            fetchData("StreetParking/parkMachines/2", "GET", "url", {
                parkMachineCode: query.value.replaceAll("ib_", ""),
            }).then((parkingMachinesList) => {
                if (!parkingMachinesList[0]) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                parkingMachinesList.forEach((machine) => {
                    addParkingMachine(JSON.parse(machine.position), "#1D4F8B", machine, false);
                });

                const feature = parkingMachines.getSource().getFeatureById(parkingMachinesList[0].parkMachineID);

                if (!feature) {
                    setAlert(t("home.MapSearchComponent.noResult"));
                    navigate("/");
                    setDetailZone(null);
                    mapService.selectFeature(null);
                    return;
                }

                if (feature instanceof Feature) {
                    const extent = feature.getGeometry().getExtent();
                    map.getView().fit(extent, {
                        size: map.getSize(),
                        padding: [100, 100, 100, 100],
                        maxZoom: maxZoom,
                        duration: mapAnimationDuration,
                    });

                    const properties = feature.getProperties();
                    mapService.selectFeature(feature);
                    feature.setStyle(parkingMachineStyle("#d40939", feature));
                    const coordinates = getCenter(feature.getGeometry().getExtent());
                    setDetailZone({
                        coordinates: coordinates,
                        properties: feature.getProperties(),
                    });
                    redirectToSection(properties, properties.parkMachineCode, "parkingMachine");
                }
            });
        }
    }, [filter.query]);

    const isZoneVisibleOnMap = useCallback(
        (sectionCode: string) => {
            return filteredZones.some((zone) => zone.sectionCode === sectionCode);
        },
        [filteredZones]
    );

    const location = useLocation();

    const isPageReloaded =
        (sessionStorage.getItem("canUseReloadNavigation") ?? "true") === "true" &&
        (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type === "reload";

    const shouldAutoLocate = location.key === "default" || isPageReloaded;

    useEffect(() => {
        sessionStorage.setItem("canUseReloadNavigation", "false");
    }, [isPageReloaded]);

    const getAutoLocation = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const view = map.getView();
                    const currentCenter = view.getCenter();
                    const [mappedLongitude, mappedLatitude] = transform([longitude, latitude], "EPSG:4326", "EPSG:5514");
                    const currentZoom = view.getZoom();

                    if (hasAutoLocation && shouldAutoLocate) {
                        view.animate(
                            {
                                center: currentCenter,
                                zoom: currentZoom,
                            },
                            {
                                center: [mappedLongitude, mappedLatitude],
                                zoom: mapZoom,
                                duration: mapAnimationDuration,
                            }
                        );
                    }

                    const featureCircle = new Feature(new Circle([mappedLongitude, mappedLatitude], 4));
                    featureCircle.setStyle(myLocationMarkerStyleCircle);

                    const featureBg = new Feature(new Circle([mappedLongitude, mappedLatitude], 8));
                    featureBg.setStyle(myLocationMarkerStyleBackground);

                    myLocationMarker
                        .getSource()
                        .getFeatures()
                        .map((feature) => {
                            myLocationMarker.getSource().removeFeature(feature);
                        });

                    myLocationMarker.getSource().addFeatures([featureBg, featureCircle]);
                },
                (error) => {
                    //console.error("Error getting location:", error);
                }
            );
        } else {
            console.error("Geolocation is not supported by this browser.");
        }
    }, [map]);

    const [mapViewCenterState, setMapViewCenterState] = useLocalStorageState("map_ViewCenter", mapViewCenter);
    const [mapViewZoomState, setMapViewZoomState] = useLocalStorageState("map_ViewZoom", mapViewZoom);
    const [hasAutoLocation, setHasAutoLocation] = useLocalStorageState("map_autoLocation", true);

    // Set map view center to local storage state
    useEffect(() => {
        if (hasAutoLocation) {
            setMapViewCenterState(mapViewCenter);
            setMapViewZoomState(mapViewZoom);
        }
    }, [mapViewCenter, setMapViewCenterState, setMapViewZoomState, mapViewZoom]);

    const redirectToSection = (properties, code: string, type: "parkingZone" | "parkingMachine" = "parkingZone") => {
        if (properties.parkMachineType === PARK_MACHINE_TYPE.infobox) {
            navigate(`/ib/${code}`);
        } else if (type === "parkingZone") {
            navigate(`/pz/${code}`);
        } else if (type === "parkingMachine") {
            navigate(`/pm/${code}`);
        }
    };

    const loadMapDataByPosition = async (isFirstLoad: boolean) => {
        const result = await mapService.loadDataByPosition(filterRef.current, isFirstLoad);

        if (!result.success) {
            if (!mapService.getSelectedFeature() && !mapService.isZoomSufficient()) {
                setHideResults(true);
                setIsMapResultForceClose(false);
                setDetailZone(null);
            }
            setFilteredZones(result.visibleZones);
            return;
        }

        setRaLevelsData((prev) => ({ ...prev, ...result.raLevelsData }));
        setFilteredZones(result.visibleZones);
        setHideResults(false);

        const sectionsData = (result as any).sectionsData;
        if (sectionsData && !mapService.validateDetailZoneInBounds(detailZone, sectionsData)) {
            navigate(`/`);
            setDetailZone(null);
            mapService.selectFeature(null);
        }
    };

    useEffect(() => {
        loadMapDataByPosition(true);
    }, []);

    const previousLocation = usePreviousLocation();

    useEffect(() => {
        if (mapRef.current) {
            map.setTarget(mapRef.current);
            map.updateSize();
        }

        initMapLayer().catch(() => setAlert(t("home.mapLoadError")));

        map.on("movestart", (evt) => {
            setMapMove(true);
        });

        map.on(
            "moveend",
            mapDebounce(async (evt) => {
                loadMapDataByPosition(false);
            }, 150)
        );

        map.on("moveend", (evt) => {
            const selectedFeature = mapService.getSelectedFeature();
            if (selectedFeature) {
                let viewExtent = map.getView().calculateExtent(map.getSize());
                let featureExtent = selectedFeature.getGeometry().getExtent();
                let isVisible = containsExtent(viewExtent, featureExtent);

                if (!isVisible && !map.getView().getAnimating() && mapMove === false) {
                    navigate(`/`);
                    setIsMapResultForceClose(false);
                    setDetailZone(null);
                    mapService.deselectFeature();
                }
            }
        });

        map.on("pointermove", (evt) => {
            const pixel = map.getEventPixel(evt.originalEvent);
            const hit = map.hasFeatureAtPixel(pixel);
            const selectedFeature = mapService.getSelectedFeature();
            let hoveredFeature = mapService.getHoveredFeature();

            if (hit) {
                const feature = map.forEachFeatureAtPixel(pixel, (feature) => feature);

                if (feature !== selectedFeature && feature instanceof Feature) {
                    if (feature.getProperties().featureType === "parkingZone") {
                        if (hoveredFeature && hoveredFeature !== selectedFeature) {
                            if (hoveredFeature.getProperties().featureType === "parkingZone") {
                                hoveredFeature.setStyle(
                                    styleElement(hoveredFeature.getProperties().sectionCategoryColor, false, false)
                                );
                            } else if (hoveredFeature.getProperties().featureType === "parkingMachine") {
                                hoveredFeature.setStyle(parkingMachineStyle("#1D4F8B", hoveredFeature));
                            }
                        }

                        feature.setStyle(styleElement(feature.getProperties().sectionCategoryColor, false, true));
                    }

                    if (feature.getProperties().featureType === "parkingMachine") {
                        if (hoveredFeature && hoveredFeature !== selectedFeature) {
                            if (hoveredFeature.getProperties().featureType === "parkingMachine") {
                                hoveredFeature.setStyle(parkingMachineStyle("#1D4F8B", hoveredFeature));
                            } else if (hoveredFeature.getProperties().featureType === "parkingZone") {
                                hoveredFeature.setStyle(
                                    styleElement(hoveredFeature.getProperties().sectionCategoryColor, false, false)
                                );
                            }
                        }

                        feature.setStyle(parkingMachineStyle("#d40939", feature));
                    }

                    mapService.setHoveredFeature(feature);
                    map.getViewport().style.cursor = "pointer";
                } else if (hoveredFeature && hoveredFeature !== selectedFeature) {
                    if (hoveredFeature.getProperties().featureType === "parkingZone") {
                        hoveredFeature.setStyle(styleElement(hoveredFeature.getProperties().sectionCategoryColor, false, false));
                    } else if (hoveredFeature.getProperties().featureType === "parkingMachine") {
                        hoveredFeature.setStyle(parkingMachineStyle("#1D4F8B", hoveredFeature));
                    }
                    mapService.setHoveredFeature(null);
                    map.getViewport().style.cursor = "";
                }
            } else if (hoveredFeature && hoveredFeature !== selectedFeature) {
                if (hoveredFeature.getProperties().featureType === "parkingZone") {
                    hoveredFeature.setStyle(styleElement(hoveredFeature.getProperties().sectionCategoryColor, false, false));
                } else if (hoveredFeature.getProperties().featureType === "parkingMachine") {
                    hoveredFeature.setStyle(parkingMachineStyle("#1D4F8B", hoveredFeature));
                }
                mapService.setHoveredFeature(null);
                map.getViewport().style.cursor = "";
            }
        });

        map.on("singleclick", (evt) => {
            const feature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);
            const selectedFeature = mapService.getSelectedFeature();

            if (selectedFeature && selectedFeature.getProperties().featureType === "parkingZone") {
                selectedFeature.setStyle(styleElement(selectedFeature.getProperties().sectionCategoryColor, false, false));
            }

            if (selectedFeature && selectedFeature.getProperties().featureType === "parkingMachine") {
                selectedFeature.setStyle(parkingMachineStyle("#1D4F8B", selectedFeature));
            }

            if (feature instanceof Feature && feature.getProperties().featureType === "parkingMachine") {
                mapService.selectFeature(null);

                const properties = feature.getProperties();
                mapService.selectFeature(feature);
                feature.setStyle(parkingMachineStyle("#d40939", feature));
                const coordinates = getCenter(feature.getGeometry().getExtent());
                setDetailZone({
                    coordinates: coordinates,
                    properties: feature.getProperties(),
                });

                redirectToSection(properties, properties.parkMachineCode, "parkingMachine");
            }

            if (feature instanceof Feature && feature.getProperties().featureType === "parkingZone") {
                const properties = feature.getProperties();

                if (properties.sectionCode !== selectedFeature?.getProperties().sectionCode) {
                    setIsMapResultForceClose(true);
                    loadDetailZone(properties, true);
                    redirectToSection(properties, properties.sectionCode);
                }

                mapService.selectFeature(feature);

                map.getViewport().style.cursor = "";
            } else if (feature?.getProperties()?.featureType !== "parkingMachine") {
                navigate(`/`);
                setIsMapResultForceClose(false);
                mapService.selectFeature(null);
                setDetailZone(null);
                setPricingDetail(false);
            }

            if (feature?.getProperties()?.sectionID && feature?.getProperties().featureType === "parkingZone") {
                const geometry = feature.getGeometry();
                const coordinates = transform(getCenter(geometry.getExtent()), "EPSG:5514", "EPSG:4326");
                setDetailZone({
                    coordinates: coordinates,
                    properties: feature.getProperties(),
                });
                redirectToSection(feature.getProperties(), feature.getProperties().sectionCode);
            } else if (feature?.getProperties()?.featureType !== "parkingMachine") {
                setDetailZone(null);
            }

            if (
                feature instanceof Feature &&
                feature?.getProperties()?.sectionID &&
                feature?.getProperties().featureType === "parkingZone"
            ) {
                mapService.selectFeature(feature);
                feature.setStyle(styleElement(feature.getProperties().sectionCategoryColor, true, null));
            }
        });

        getAutoLocation();

        // Při nákupu zpět na pozici na mapě, kde byl před tím
        if (!hasAutoLocation) {
            if (previousLocation.startsWith("/payment/") || previousLocation.startsWith("/paid/")) {
                map.getView().setCenter(mapViewCenterState);
                map.getView().setZoom(mapViewZoomState);
                setHasAutoLocation(true);
            } else {
                setHasAutoLocation(true);
                getAutoLocation();
            }
        }
    }, [map, getAutoLocation]);

    useEffect(() => {
        const selectedFeature = mapService.getSelectedFeature();
        if (
            selectedFeature &&
            !location.pathname.startsWith("/pz/") &&
            !location.pathname.startsWith("/pm/") &&
            !location.pathname.startsWith("/ib/")
        ) {
            mapService.deselectFeature();
            setDetailZone(null);
        }
    }, [location]);

    const [filterIsOpened, setFilterIsOpened] = useState(false);

    const [showQrCodeReader, setShowQrCodeReader] = useState(false);

    useEffect(() => {
        if (showQrCodeReader) {
            setDetailZone(null);
        }
    }, [showQrCodeReader, detailZone]);

    const handleScan = async (data) => {
        if (data.startsWith("https://parking.praha.eu/PA/")) {
            const parkingMachineQR = data.replace("https://parking.praha.eu/PA/", "");

            setShowQrCodeReader(false);

            const parkingMachinesList = await fetchData("StreetParking/parkMachines/2", "GET", "url", {
                parkMachineCode: parkingMachineQR,
            });

            if (!parkingMachinesList[0]) {
                return;
            }

            setSelectedParkingMachine(parkingMachinesList[0].parkMachineID);

            const results = await streetParkingService.getStreetParkingSection(filter.query.value.replaceAll("api_", ""));

            if (!results[0]) {
                return;
            }

            await loadParkingZonesIntoMap(results, false);

            const feature = parkingZones.getSource().getFeatureById(results[0].sectionID);

            if (feature instanceof Feature) {
                loadDetailZone(feature.getProperties(), false);
                mapService.selectFeature(feature);
                redirectToSection(feature.getProperties(), feature.getProperties().sectionCode);
                const geometry = feature.getGeometry();
                const coordinates: Coordinate = transform(getCenter(geometry.getExtent()), "EPSG:5514", "EPSG:4326");
                setDetailZone({
                    coordinates: coordinates,
                    properties: feature.getProperties(),
                });
            }
        }
    };

    const handleFilterChange = useCallback((values) => {
        setFilter(values);
    }, []);

    const handleOpenFilter = useCallback(() => {
        setSnapPoints([-500, 100]);
        setFilterIsOpened(true);
    }, []);

    const handleCloseFilter = useCallback(() => {
        setSnapPoints([-195, 100]);
        setFilterIsOpened(false);
    }, []);

    const handleQrCodeClick = useCallback(() => {
        setShowQrCodeReader(true);
    }, []);

    const [version, setVersion] = useState(0);

    const forceReload = () => {
        setVersion((v) => v + 1);
    };

    return (
        <div className={styles.container}>
            {alert && <Alert message={alert} onClose={() => setAlert(null)} />}
            {detailZone && (
                <div
                    className={styles.parkingZoneDetail}
                    style={{ border: "1px solid #666666E5" }}
                    //style={{"border": "1px solid " + selectedFeature?.getProperties().sectionCategoryColor}}
                    id="parkingZoneDetail"
                    ref={detailZoneRef}
                >
                    <div
                        className={styles.closeOverlay}
                        onClick={() => {
                            navigate(`/`);
                            setIsMapResultForceClose(false);
                            mapService.deselectFeature();
                            setDetailZone(null);
                            setPricingDetail(false);
                        }}
                    >
                        <Icon.RemoveTiny size={isMobile ? 18 : 12} color={"white"} />
                    </div>

                    <DetailZoneName
                        code={
                            detailZone.properties.featureType === "parkingZone"
                                ? detailZone.properties.sectionCode
                                : detailZone.properties.parkMachineCode
                        }
                        sectionCategoryName={detailZone.properties.sectionCategoryName}
                        featureType={detailZone.properties.featureType}
                        sectionID={detailZone.properties.sectionID}
                    />

                    {detailZone.properties.streetName && (
                        <DetailZoneItem
                            type={"street"}
                            value={detailZone.properties.streetName}
                            properties={detailZone.properties}
                        />
                    )}

                    <DetailZoneItem
                        type={"time"}
                        value={detailZone.properties.tariffInfo?.maxHour}
                        properties={detailZone.properties}
                    />

                    <DetailZoneItem
                        type="pricing"
                        value={detailZonePricing}
                        subValue={sortTariffDefinitions(detailZone.properties.tariffInfo)}
                        id={detailZone.properties.sectionCode}
                        onTogglePricingDetail={(state) => setPricingDetail(state)}
                        pricingDetailState={pricingDetail}
                        properties={detailZone.properties}
                    />

                    <DetailZoneActions
                        sectionCode={
                            detailZone.properties.featureType === "parkingZone"
                                ? detailZone.properties.sectionCode
                                : detailZone.properties.parkMachineCode
                        }
                        featureType={detailZone.properties.featureType}
                        section={mapService.getSelectedFeature()?.getProperties()}
                        detailZone={detailZone}
                        sectionId={
                            detailZone.properties.featureType === "parkingZone"
                                ? mapService.getSelectedFeature()?.getProperties()?.sectionID
                                : mapService.getSelectedFeature()?.getProperties()?.parkMachineID
                        }
                        properties={detailZone.properties}
                        forceReload={forceReload}
                    />
                </div>
            )}

            {showQrCodeReader ? (
                <div className={styles.qrCodeOverlay} onClick={() => setShowQrCodeReader(false)}>
                    <div className={styles.qrCodeContainer}>
                        <div className={styles.qrCodecloseOverlay} onClick={() => setShowQrCodeReader(false)}>
                            <Icon.RemoveTiny size={isMobile ? 18 : 12} color="white" />
                        </div>

                        <p className={styles.qrCodeText}>{t("home.QRCode.description")}</p>
                        <QrReader
                            onResult={(result, error) => {
                                if (result) {
                                    handleScan(result.getText());
                                }
                            }}
                            className={styles.qrReader}
                            constraints={{ facingMode: "environment", width: 300, height: 300 }}
                        />
                    </div>
                </div>
            ) : null}

            <div className={styles.map}>
                <div className={styles.mapContainer}>
                    <div id="map" className={styles.map} ref={mapRef}></div>
                </div>
            </div>

            {(isDesktop || (isMobile && detailZone === null)) && (
                <div className={styles.sidePanel}>
                    <MapSearch
                        filter={filter}
                        isZoneVisibleOnMap={isZoneVisibleOnMap}
                        onChangeFilter={handleFilterChange}
                        wrapperClassName={styles.mapSearch}
                        onOpenFilter={handleOpenFilter}
                        onCloseFilter={handleCloseFilter}
                        onQrCodeClick={handleQrCodeClick}
                        dependencies={{ arcgisService, streetParkingService }}
                    />

                    {!filterIsOpened && <MapMyLocation showOn={"mobile"} />}

                    <MapResults
                        wrapperClassName={`${styles.mapResults} ${styles.showOnDesktop} ${
                            filterIsOpened ? styles.filterIsOpened : ""
                        }`}
                        filteredResults={filteredZones}
                        hideResults={hideResults}
                        raLevels={raLevelsData}
                        currentSectionCode={
                            detailZone?.properties.featureType === "parkingZone"
                                ? (detailZone?.properties.sectionID ?? null)
                                : (detailZone?.properties.parkMachineID ?? null)
                        }
                        onSelect={(section) => {
                            locationMarker.getSource().clear();
                            loadDetailZone(section, true);
                        }}
                        version={version}
                        forceReload={forceReload}
                    />
                </div>
            )}

            <div id="mapControls">
                {searchParams.get("zoomValue") === "true" ? (
                    <p
                        style={{
                            position: "absolute",
                            right: 16,
                            bottom: 120,
                            zIndex: "999999",
                            background: "white",
                        }}
                    >
                        Zoom: {map.getView().getZoom()}
                    </p>
                ) : null}

                {!detailZone ? (
                    <div className={styles.zoomPanel}>
                        {((isMobile && !filterIsOpened) || isDesktop) && (
                            <div className={styles.zoomButtonContainer}>
                                <MapZoom action={"in"} step={1} />
                                <MapZoom action={"out"} step={1} />
                            </div>
                        )}
                    </div>
                ) : null}

                {!detailZone ? (
                    <div className={styles.locationPanel}>
                        <MapMyLocation showOn={"desktop"} />
                    </div>
                ) : null}
                {!detailZone ? (
                    <MapResultsSheet
                        forceClose={isMapResultsForceClose}
                        filteredZones={filteredZones}
                        hideResults={hideResults}
                        snapPoint={snapPoints}
                        raLevelsData={raLevelsData}
                        onSelect={(section) => {
                            loadDetailZone(section, true);
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default Main;
